<script setup>
import { storeToRefs } from "pinia";
import {
  computed,
  ref,
  onBeforeMount,
  onUnmounted,
  nextTick,
  watch } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import AgentLayout from "@/views/layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import AgentEmotion from "@/components/shared/AgentEmotion.vue";
import { useUiStore } from "@/stores/ui";
import { useAgentsStore } from "@/stores/agents";
import { useConversationsStore } from "@/stores/conversations";
// Add markdown parser
import { marked } from "marked";
import productPlaceholder from "@/assets/images/product_placeholder.png";
import LiveKitService from "@/utils/livekit";
import { useCompanyStore } from "@/stores/company";
const { companyId } = storeToRefs(useCompanyStore());
const userDetails = ref(JSON.parse(localStorage.getItem("user")));
const superAdminEmail = process.env.VUE_APP_SUPER_ADMIN_EMAIL;
const uiStore = useUiStore();
const agentsStore = useAgentsStore();
const conversationsStore = useConversationsStore();
let agentObj = ref({});
let selectedAgentId = ref(null);
let storeObj = ref(null);
let selectedStoreId = ref(null);
let currentConversation = ref({});
let agentsArr = ref([]);
let storesOptions = ref([]);
let defaultStoreId = ref(null);
let defaultSiteId = ref(null);
let superAdmin = computed(() => {
  return userDetails.value.user.email === superAdminEmail;
});
const activeTab = ref('messages');
const isRecording = ref(false);
let mediaRecorder = null;
let livekitService = null; // LiveKit service instance
let lastConnectedConversationId = null; // Track last connected conversation
let lastConnectedAgentId = null; // Track last connected agent
let isConnectingToLiveKit = false; // Flag to prevent concurrent connections
const isLoadingToConnectToLiveKit = ref(false);
const livekitConnected = ref(false); // Track if LiveKit is currently connected
let idleTimeout = null; // Timeout for idle disconnect (1 minute)
let audioStream = null;
let audioContext = null;
let silenceTimer = null;
let conversationId = ref(null); // Track current conversation ID
let isLoading = ref(true);
let isLoadingHistory = ref(false); // Loading state for conversation history
const LIVEKIT_URL = ref(null);
const userInput = ref("");
const RECONNECT_BASE_DELAY_MS = 2000;
const IDLE_TIMEOUT_MS = 60000;
const CONNECT_DELAY_MS = 500;

// Configure marked for better formatting
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

// Helper function to process message content
const processMessageContent = (content) => {
  if (!content) return "";

  // Replace \n\n with actual double line breaks for markdown
  // Replace \n with single line breaks
  const processedContent = content
    .replace(/\\n\\n/g, "\n\n") // Handle escaped double newlines
    .replace(/\\n/g, "\n") // Handle escaped single newlines
    .replace(/\n\n/g, "\n\n") // Handle actual double newlines
    .replace(/\n/g, "\n"); // Handle actual single newlines

  // Convert to markdown HTML
  return marked(processedContent);
};

const processMessageContentLogs = (content) => {
  if (!content) return "";

  // Replace \n\n with actual double line breaks for markdown
  // Replace \n with single line breaks
  const processedContent = content
    .replace(/\\n\\n/g, "\n\n") // Handle escaped double newlines
    .replace(/\\n/g, "\n") // Handle escaped single newlines
    .replace(/\n\n/g, "\n\n") // Handle actual double newlines
    .replace(/\n/g, "\n"); // Handle actual single newlines

  // Convert to markdown HTML
  return marked(processedContent);
};

// Helper function to format timestamps in browser timezone
const formatTimestamp = (timestamp = null) => {
  const date = timestamp ? new Date(timestamp) : new Date();
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Get current date for comparison
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  // Check if message is from today
  if (messageDate.getTime() === today.getTime()) {
    // Show only time for today's messages
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } else {
    // Show date and time for older messages
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
};

// Starts empty — the greeting is the agent's own welcome_message, delivered
// as a real assistant_text message over the LiveKit socket once connected.
// No local placeholder is shown in the meantime.
const messages = ref([]);

const logs = ref([]);
const conversationsArr = ref([]);

const isTyping = ref(false);
const messagesContainer = ref(null);
const logsContainer = ref(null);

// Whether new messages should pull the view down to the latest one. Stays
// true while the user is at (or near) the bottom; a manual scroll up turns
// it off so they can read older messages without being yanked back down.
const shouldAutoScroll = ref(true);
const AUTO_SCROLL_THRESHOLD_PX = 80;

function onMessagesScroll() {
  const el = messagesContainer.value;
  if (!el) return;
  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
  shouldAutoScroll.value = distanceFromBottom <= AUTO_SCROLL_THRESHOLD_PX;
}

// Auto-scroll to bottom function. Pass force=true to always snap to the
// bottom and resume auto-scrolling (e.g. right when the user sends a
// message) regardless of where they'd scrolled to.
const scrollToBottom = async (force = false) => {
  if (force) {
    shouldAutoScroll.value = true;
  }
  await nextTick();
  if (messagesContainer.value && shouldAutoScroll.value) {
    try {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    } catch (error) {
      console.error("Error scrolling to bottom:", error);
    }
  }
};

watch(
  () => messages.value.length,
  () => {
    nextTick(() => scrollToBottom());
    resetIdleTimer();
  },
);

const sendMessage = async () => {
  if (!userInput.value.trim()) return;

  const messageText = userInput.value;

  // Add user message
  messages.value.push({
    id: messages.value.length + 1,
    sender: "user",
    content: messageText,
    timestamp: formatTimestamp(),
  });

  // Show typing indicator
  isTyping.value = true;

  // Auto-scroll after adding the user message and showing the thinking
  // indicator (force it so both are visible even if the user had scrolled
  // up earlier)
  await scrollToBottom(true);

  // Clear input
  userInput.value = "";

  // Ensure LiveKit is connected before sending
  if (!livekitService || !livekitService.isConnected) {
    try {
      await updateLiveKitConnection();
      // Wait a moment for connection to establish
      await new Promise((resolve) => setTimeout(resolve, CONNECT_DELAY_MS));

      if (!livekitService || !livekitService.isConnected) {
        throw new Error("Failed to establish connection");
      }
    } catch (error) {
      console.error("Failed to setup LiveKit:", error);
      messages.value.push({
        id: messages.value.length + 1,
        sender: "system",
        content: "Unable to send message. Please check your connection.",
        timestamp: formatTimestamp(),
      });
      isTyping.value = false;
      return;
    }
  }

  // Send message through LiveKit
  try {
    await livekitService.sendMessage(messageText);
    // Reset idle timer on message send
    resetIdleTimer();
  } catch (error) {
    console.error("Error sending message:", error);
    // Show error message to user
    messages.value.push({
      id: messages.value.length + 1,
      sender: "system",
      content: "Failed to send message. Please try again.",
      timestamp: formatTimestamp(),
    });
    isTyping.value = false;
  }
};

// LiveKit reconnection configuration
const MAX_RETRIES = 5;
let retryCount = 0;
let isAgentSwitch = false; // Track if this is an agent switch
let isReconnectingAfterIdle = false; // Track if reconnecting after idle/intentional disconnect

const updateLiveKitConnection = async () => {
  // Prevent concurrent connection attempts
  if (isConnectingToLiveKit) {
    return;
  }

  // Check if already connected to the same conversation and agent
  if (
    livekitService &&
    livekitService.isConnected &&
    lastConnectedConversationId === conversationId.value &&
    lastConnectedAgentId === agentObj.value?.id
  ) {    return;
  }

  // Set connecting flag
  isConnectingToLiveKit = true;
  isLoadingToConnectToLiveKit.value = true;

  try {
    // Disconnect existing LiveKit connection if conversation or agent changed
    if (livekitService) {
      // Set flag to prevent reconnection messages during intentional disconnect
      isAgentSwitch = true;
      await livekitService.disconnect();
      livekitService.cleanup();
      livekitService = null;
      lastConnectedConversationId = null;
      lastConnectedAgentId = null;
    }

    // Only setup LiveKit if we have all required data
    if (
      !agentObj.value ||
      !agentObj.value.id ||
      !conversationId.value ||
      !defaultSiteId.value
    ) {      return;
    }    isReconnectingAfterIdle = isAgentSwitch;

    // Reset retry counter and agent switch flag
    retryCount = 0;
    isAgentSwitch = false;

    // Setup new LiveKit connection
    await setupLiveKit();

    // Track the connected conversation and agent
    lastConnectedConversationId = conversationId.value;
    lastConnectedAgentId = agentObj.value.id;
  } finally {
    // Always clear connecting flag
    isConnectingToLiveKit = false;
    // Set loading state to false
    isLoadingToConnectToLiveKit.value = false;
  }
};

const setupLiveKit = async () => {
  try {
    // Get LiveKit token from backend
    const token = await getLiveKitToken();

    if (!token) {
      throw new Error("Failed to get LiveKit token");
    }


    // Create new LiveKit service instance
    livekitService = new LiveKitService();

    // Setup message handler
    livekitService.onMessage(async (response) => {
      if (response.type === "card-item" && Array.isArray(response.content)) {
        if (response.content.length > 0) {
          messages.value.push({
            id: Date.now(),
            sender: "agent",
            type: "attachment-grid",
            attachments: response.content.map((item) => ({
              title: item.title,
              image_url:
                item.images_urls && item.images_urls.length > 0
                  ? item.images_urls[0]
                  : null,
              product_url: item.product_url,
              price: item.price,
              brand: item.brand,
            })),
            isPartial: false,
            timestamp: formatTimestamp(),
          });
        }
        isTyping.value = false;
        await scrollToBottom();
        return;
      } else if (response.type === "map-item" && response.content) {
        messages.value.push({
          id: Date.now(),
          sender: "agent",
          type: "map-image",
          image_url:
            response.content && response.content[0]?.map_url
              ? response.content[0].map_url
              : "",
          isPartial: false,
          timestamp: formatTimestamp(),
        });
        isTyping.value = false;
        await scrollToBottom();
        return;
      }

      if (response.type === "logs" && response.content) {
        logs.value.push({
          id: Date.now() + Math.random(),
          sender: "agent",
          content: response.content,
          isPartial: false,
          timestamp: formatTimestamp(),
        });

        setTimeout(() => {
          if (logsContainer.value) {
            logsContainer.value.scrollTop = logsContainer.value.scrollHeight;
          }
        }, 100);
        return;
      }

      // Add the chunk to messages if it contains content
      if (response.type === "assistant_text" && response.content) {
        // Stop typing indicator immediately when we receive content
        isTyping.value = false;

        // Check if response contains the end marker
        const endMarkerIndex = response.content.indexOf("<end>");
        const content =
          endMarkerIndex !== -1
            ? response.content.substring(0, endMarkerIndex)
            : response.content;

        // If the last message is from the agent and marked as partial, update it
        const lastMessage = messages.value[messages.value.length - 1];
        if (
          lastMessage &&
          lastMessage.sender === "agent" &&
          lastMessage.isPartial
        ) {
          lastMessage.content += content;
          // Keep following along as the response streams in (no-op if the
          // user has scrolled up to read earlier messages)
          await scrollToBottom();
          // Update message completion status
          if (endMarkerIndex !== -1 || response.done) {
            lastMessage.isPartial = false;
            setTimeout(scrollToBottom, 100);
          }
        } else {
          // Add new message
          messages.value.push({
            id: messages.value.length + 1,
            sender: "agent",
            content: content,
            isPartial: !(endMarkerIndex !== -1 || response.done),
            timestamp: formatTimestamp(),
          });
          // Scroll to bottom
          if (endMarkerIndex !== -1 || response.done) {
            setTimeout(scrollToBottom, 100);
          }
        }
      }
    });

    // Setup log handler
    // livekitService.onLog((response) => {
    //    //   const content =
    //     endMarkerIndex !== -1
    //       ? response.content.substring(0, endMarkerIndex)
    //       : response.content;

    //   logs.value.push({
    //     id: Date.now() + Math.random(),
    //     sender: "agent",
    //     content: content,
    //     isPartial: false,
    //     timestamp: formatTimestamp(),
    //   });

    // });

    // Setup connection handler
    livekitService.onConnection((isConnected) => {
      if (isConnected) {
        retryCount = 0;

        // Show reconnection message if this was after idle disconnect
        if (isReconnectingAfterIdle) {
          // messages.value.push({
          //   id: messages.value.length + 1,
          //   sender: 'system',
          //   content: 'Connection restored. You can continue chatting.',
          //   timestamp: formatTimestamp()
          // });
          isReconnectingAfterIdle = false;
        }

        isAgentSwitch = false;
        livekitConnected.value = true;
        // Show the thinking indicator right away — it stays up until the
        // agent's first response (greeting or reply) actually arrives.
        isTyping.value = true;
        // If this conversation already has history, jump straight to the
        // bottom so the upcoming response is visible instead of leaving the
        // view wherever it happened to be scrolled while behind the overlay.
        scrollToBottom(true);
        startIdleTimer();
      } else {
        livekitConnected.value = false;
        clearIdleTimer();

        // Only attempt reconnection if we haven't exceeded max retries AND not intentionally disconnected
        if (retryCount < MAX_RETRIES && !isAgentSwitch) {          messages.value.push({
            id: messages.value.length + 1,
            sender: "system",
            content: `Connection lost. Attempting to reconnect... (${
              retryCount + 1
            }/${MAX_RETRIES})`,
            timestamp: formatTimestamp(),
          });

          setTimeout(async () => {
            retryCount++;
            await setupLiveKit();
          }, RECONNECT_BASE_DELAY_MS * retryCount);
        } else if (retryCount >= MAX_RETRIES && !isAgentSwitch) {
          messages.value.push({
            id: messages.value.length + 1,
            sender: "system",
            content: 'Connection lost. Please click "Go" to reconnect.',
            timestamp: formatTimestamp(),
          });
        }
        // If isAgentSwitch is true, we skip reconnection (idle timeout or page leave)
      }
    });

    // Connect to LiveKit room
    await livekitService.connect(LIVEKIT_URL.value, token);

  } catch (error) {
    console.error("Error setting up LiveKit:", error);
    livekitConnected.value = false;
    isTyping.value = false;
    clearIdleTimer();

    // Show more detailed error message
    let errorMessage = "Failed to connect";

    if (error.message?.includes("token")) {
      errorMessage =
        "Failed to get authentication token. Please check backend configuration.";
    } else if (
      error.message?.includes("could not establish signal connection")
    ) {
      errorMessage = `Cannot connect to LiveKit server at ${LIVEKIT_URL.value}. Please check if the server is running.`;
    } else if (error.response) {
      errorMessage = `Server error: ${
        error.response.data?.message || error.message
      }`;
    }

    messages.value.push({
      id: messages.value.length + 1,
      sender: "system",
      content: errorMessage,
      timestamp: formatTimestamp(),
    });

    // Don't retry on token errors
    if (!error.message?.includes("token")) {
      throw error;
    }
  }
};

// Idle timer management
const clearIdleTimer = () => {
  if (idleTimeout) {
    clearTimeout(idleTimeout);
    idleTimeout = null;
  }
};

const startIdleTimer = () => {
  clearIdleTimer();
  // Set 1 minute (60000ms) idle timeout
  idleTimeout = setTimeout(async () => {
    if (livekitService) {
      try {
        // Set flag to prevent reconnection attempts
        isAgentSwitch = true;
        await livekitService.disconnect();
        livekitService.cleanup();
        livekitService = null;
        lastConnectedConversationId = null;
        lastConnectedAgentId = null;
        livekitConnected.value = false;
        isConnectingToLiveKit = false;

        // Notify user
        messages.value.push({
          id: messages.value.length + 1,
          sender: "system",
          content:
            'Connection closed due to inactivity. Click "Go" to reconnect.',
          timestamp: formatTimestamp(),
        });
      } catch (e) {
        console.error("Error during idle disconnect:", e);
      }
    }
    clearIdleTimer();
  }, IDLE_TIMEOUT_MS);
};

const resetIdleTimer = () => {
  if (livekitConnected.value) {
    startIdleTimer();
  }
};

// Function to start LiveKit connection (called by Go button)
const startLiveKit = async () => {
  try {
    await updateLiveKitConnection();
  } catch (e) {
    console.error("Error starting LiveKit from Go button:", e);
  }
};

// Get LiveKit token from backend
const getLiveKitToken = async () => {
  try {
    const response = await conversationsStore.getLiveKitToken({
      company_uuid: companyId.value,
      user_uuid: userDetails.value.user.id,
      agent_uuid: agentObj.value.id,
      site_uuid: defaultSiteId.value,
      store_uuid: defaultStoreId.value,
      conversation_uuid: conversationId.value,
      show_logs: superAdmin.value,
    });

    if (!response.access_token) {
      console.error("No token found in response:", response.access_token);
      throw new Error("Invalid token response from server");
    }
    LIVEKIT_URL.value = response.url;
    return response.access_token;
  } catch (error) {
    console.error("Error getting LiveKit token:", error);
    throw error;
  }
};

const fetchAgentsSimple = async () => {
  isLoading.value = true;
  return agentsStore
    .fetchAgentsHaveStores(companyId.value)
    .then(async (res) => {
      agentsArr.value = await res.data.map((row) => {
        row.status_text =
          row.status.charAt(0).toUpperCase() +
          row.status.slice(1).toLowerCase();
        return row;
      });

      if (agentsArr.value && agentsArr.value.length) {
        agentObj.value = agentsArr.value[0];
        selectedAgentId.value = agentObj.value.id;


        storesOptions.value = agentObj.value.sites;
        storeObj.value = storesOptions.value[0];
        selectedStoreId.value = storeObj.value?.id;

        if (storeObj.value) {
          defaultStoreId.value = storeObj.value.int_store_id;
          defaultSiteId.value = storeObj.value.id;
        }

        // Fetch conversations after agent is selected
        fetchAllConversations();
      }
      isLoading.value = false;
    })
    .catch(() => {
      isLoading.value = false;
    });
};

const fetchAllConversations = async () => {
  if (!agentObj.value || !agentObj.value.id) {
    return Promise.resolve();
  }

  return conversationsStore
    .fetchAllConversations({
      companyId: companyId.value,
      params: {
        user_uuid: superAdmin.value ? null : userDetails.value.user.id,
        agent_uuid: agentObj.value.id,
        limit: 0,
        skip: 0,
      },
    })
    .then((res) => {

      if (res && Array.isArray(res) && res.length > 0) {
        conversationsArr.value = res;
        // Load the first conversation's history
        return fetchHistoryOfConversation(res[0]);
      } else {
        // No conversations found — create a new one; the greeting arrives
        // over the socket once connected, so no local message is shown yet.
        conversationsArr.value = [];
        messages.value = [];
        return createConversation();
      }
    })
    .catch(() => {
      conversationsArr.value = [];
      messages.value = [];
      return Promise.resolve();
    });
};
const fetchHistoryOfConversation = async (conversation) => {
  isLoadingHistory.value = true; // Start loading
  return conversationsStore
    .fetchHistoryOfConversation({
      companyId: companyId.value,
      conversation_uuid: conversation.uuid,
      params: {
        agent_uuid: agentObj.value.id,
        user_uuid: superAdmin.value ? null : userDetails.value.user.id,
      },
    })
    .then(async (res) => {
      currentConversation.value = conversation;
      // Set the current conversation ID
      conversationId.value = conversation.uuid;

      // Transform the API response to match chat box format
      if (res && Array.isArray(res) && res.length > 0) {
        messages.value = res.map((message) => {
          // Enhanced content extraction logic
          let content = "";
          let messageType = message.type;
          let attachments = null;

          if (message.content) {
            // Check if content is an object (like user messages that come as {"message":"text","type":"user_message"})
            if (
              typeof message.content === "object" &&
              message.content !== null
            ) {
              if (message.content.message) {
                content = message.content.message;
              } else if (message.content.content) {
                content = message.content.content;
              } else {
                // Fallback: stringify the object
                content = JSON.stringify(message.content);
              }
            } else {
              // Content is already a string
              content = message.content;
            }
          }

          // Handle attachment type with array content - set as attachment-grid
          if (
            message.type === "map" &&
            Array.isArray(message.content) &&
            message.content[0]?.map_url
          ) {
            messageType = "map-image";
            content = message.content[0].map_url;
          } else if (
            message.type === "card" &&
            Array.isArray(message.content) &&
            message.content.length > 0
          ) {
            messageType = "attachment-grid";
            attachments = message.content.map((item) => ({
              title: item.title,
              image_url: item.images_urls && item.images_urls.length > 0
                ? item.images_urls[0]
                : null,
              product_url: item.product_url,
              price: item.price,
              brand: item.brand,
            }));
            content = ""; // Clear content for attachment-grid messages
          } else if (message.type === "card") {
            // Empty products array — skip rendering this message entirely
            return null;
          }

          const transformedMessage = {
            // id: message.id || index + 1,
            sender:
              message.role === "assistant"
                ? "agent"
                : message.role === "user"
                ? "user"
                : "system",
            content: content,
            timestamp: formatTimestamp(message.timestamp),
            type: messageType,
            ...(messageType === "map-image" && { image_url: content }), // Add image_url for map type messages
          };

          // Add attachments property if it exists
          if (attachments) {
            transformedMessage.attachments = attachments;
          }
          return transformedMessage;
        }).filter(Boolean);
      } else {
        // No history yet — the greeting arrives over the socket once connected.
        messages.value = [];
      }

      isLoadingHistory.value = false; // End loading

      // Setup LiveKit connection after loading history if user already started it
      if (livekitConnected.value) {
        await updateLiveKitConnection();
      }
    })
    .catch(() => {
      isLoadingHistory.value = false; // End loading on error
      messages.value = [];
    });
};

const createConversation = () => {
  return conversationsStore
    .createConversation({
      companyId: companyId.value,
      user_uuid: userDetails.value.user.id,
      agent_uuid: agentObj.value.id,
    })
    .then(async (res) => {
      currentConversation.value = res;
      conversationId.value = res.uuid;
      // The greeting arrives over the socket once connected — no local placeholder.
      messages.value = [];
      await updateLiveKitConnection();
    })
    .catch(() => {});
};

onUnmounted(async () => {
  clearIdleTimer();
  stopRecording();
  // Disconnect LiveKit with same behavior as inactivity
  if (livekitService) {
    try {
      isAgentSwitch = true; // Prevent reconnection attempts
      await livekitService.disconnect();
      livekitService.cleanup();
      livekitService = null;
      lastConnectedConversationId = null;
      lastConnectedAgentId = null;
      isConnectingToLiveKit = false;
      livekitConnected.value = false;
    } catch (e) {
      console.error("Error during unmount disconnect:", e);
    }
  }
});

// Handle navigation away from page
onBeforeRouteLeave(async (to, from, next) => {
  clearIdleTimer();
  if (livekitService) {
    try {
      isAgentSwitch = true; // Prevent reconnection attempts
      await livekitService.disconnect();
      livekitService.cleanup();
      livekitService = null;
      lastConnectedConversationId = null;
      lastConnectedAgentId = null;
      isConnectingToLiveKit = false;
      livekitConnected.value = false;
    } catch (e) {
      console.error("Error during route leave disconnect:", e);
    }
  }
  next();
});

const resetStoreData = () => {
  uiStore.setModalState(false);
  uiStore.setModalData({});
  uiStore.setModalAction(null);
};

onBeforeMount(async () => {
  resetStoreData();
  await fetchAgentsSimple();
});

// const startRecording = async () => {
//   transcript.value = ''
//   showVoicePopup.value = true
//   voiceStage.value = 'listening'

//   // Ensure LiveKit is connected before starting recording
// if (!livekitService || !livekitService.isConnected) {
////       await updateLiveKitConnection();
//       // Wait a moment for connection to establish
//       await new Promise(resolve => setTimeout(resolve, 500));

// if (!livekitService || !livekitService.isConnected) {
//         throw new Error('Failed to establish connection');
//       }
//     } catch (error) {
//       console.error('Failed to setup LiveKit:', error);
//       messages.value.push({
//         id: messages.value.length + 1,
//         sender: 'system',
//         content: 'Unable to start voice recording. Please check your connection.',
//         timestamp: formatTimestamp()
//       });
//       showVoicePopup.value = false;
//       return;
//     }
//   }

//   audioStream = await navigator.mediaDevices.getUserMedia({ audio: true })

//   mediaRecorder = new MediaRecorder(audioStream, {
//     mimeType: 'audio/webm',
//   })

//   // Stream audio chunks in real-time
//   mediaRecorder.ondataavailable = async (event) => {
// if (event.data.size > 0 && livekitService && livekitService.isConnected) {
//       try {
//         // Send each chunk immediately as it becomes available
//         await livekitService.sendAudioData(event.data);
////         console.error('Error streaming audio chunk:', error);
//       }
//     }
//   }

//   mediaRecorder.onstop = async () => {
////     // Show typing indicator while waiting for voice response
//     isTyping.value = true;
//   }

//   // Start recording with timeslice to get chunks every 100ms for streaming
//   mediaRecorder.start(100);
//   isRecording.value = true
//   setupSilenceDetection(audioStream)
// }

const stopRecording = () => {
  if (
    mediaRecorder &&
    mediaRecorder.state &&
    mediaRecorder.state !== "inactive"
  ) {
    mediaRecorder.stop();
  }
  if (audioStream) {
    audioStream.getTracks().forEach((track) => track.stop());
  }
  isRecording.value = false;
  stopSilenceDetection();
};

// Stops and clears all audio, but does NOT play anything (for when user resumes speaking)
// const stopAndClearAllAudio = () => {
// if (window._currentVoiceAudio) {
//     window._currentVoiceAudio.pause();
//     window._currentVoiceAudio.currentTime = 0;
//     window._currentVoiceAudio = null;
//   }
//   audioResponseQueue.length = 0;
//   // isPlayingAudio = false; // Will be used when audio response handling is implemented
//   // Set voiceStage to 'listening' to ensure UI is correct
//   voiceStage.value = 'listening';
// };

// const setupSilenceDetection = (stream) => {
//   audioContext = new (window.AudioContext || window.webkitAudioContext)();
//   const source = audioContext.createMediaStreamSource(stream)
//   analyser = audioContext.createAnalyser()
//   analyser.fftSize = 2048
//   source.connect(analyser)

//   const data = new Uint8Array(analyser.fftSize)
//   let silenceStart = null
//   let wasSilent = false
//   let waitingForSpeech = false

//   const checkVolume = () => {
//     analyser.getByteTimeDomainData(data)
//     let sum = 0
//     for (let i = 0; i < data.length; i++) {
//       const value = (data[i] - 128) / 128
//       sum += value * value
//     }
//     const volume = Math.sqrt(sum / data.length)

// if (!waitingForSpeech) {
// if (volume < SILENCE_THRESHOLD) {
//         if (!silenceStart) silenceStart = Date.now()
//         else if (Date.now() - silenceStart > SILENCE_DURATION) {
//           // Silence detected, stop and send audio
// if (mediaRecorder && mediaRecorder.state === 'recording') {
//             mediaRecorder.stop();
//           }
//           wasSilent = true;
//           waitingForSpeech = true;
//           silenceStart = null;
//           requestAnimationFrame(checkVolume);
//           return;
//         }
//       } else {
//         // User started speaking again
// if (wasSilent) {
//           // Always stop and clear all audio immediately
//           stopAndClearAllAudio();
//           wasSilent = false;
//           // Start a new recording if not already recording
// if (mediaRecorder && mediaRecorder.state !== 'recording') {
//             mediaRecorder.start();
//             isRecording.value = true;
//           }
//         }
//         silenceStart = null
//       }
//       silenceTimer = requestAnimationFrame(checkVolume)
//     } else {
//       // Waiting for speech to start a new recording
// if (volume > SILENCE_THRESHOLD) {
//         // Always stop and clear all audio immediately
//         stopAndClearAllAudio();
//         wasSilent = false;
//         waitingForSpeech = false;
//         // Start a new recording if not already recording
// if (mediaRecorder && mediaRecorder.state !== 'recording') {
//           mediaRecorder.start();
//           isRecording.value = true;
//         }
//       }
//       silenceTimer = requestAnimationFrame(checkVolume)
//     }
//   }

//   checkVolume()
// }

const stopSilenceDetection = () => {
  if (audioContext) {
    audioContext.close();
    audioContext = null;
  }
  if (silenceTimer) {
    cancelAnimationFrame(silenceTimer);
    silenceTimer = null;
  }
};

// const activeAgent = async (agent) => {
//   agentObj.value = agent;
//   // Set flag to indicate this is an agent switch
//   isAgentSwitch = true;
//   await fetchAllConversations();
//   updateSocketConnection();
//   setupTextWebSocket();
// }

const activeAgent = async () => {
  // Set flag to indicate this is an agent switch
  isAgentSwitch = true;
  await setDefaultStoreId();
  await fetchAllConversations();
  startLiveKit();
  // LiveKit setup is handled in fetchHistoryOfConversation or createConversation
};

const setDefaultStoreId = () => {
  defaultStoreId.value = storeObj.value.int_store_id;
  defaultSiteId.value = storeObj.value.id;
};

const changeAgent = (event) => {

  agentObj.value = agentsArr.value.find((agent) => agent.id === event.value);

  storesOptions.value = [];
  storeObj.value = null;
  defaultStoreId.value = null;
  defaultSiteId.value = null;
  selectedStoreId.value = null;
  if (agentObj.value) {
    storesOptions.value = agentObj.value.sites;
    selectedAgentId.value = agentObj.value.id;
  }
};

const changeStore = (event) => {
  storeObj.value = null;
  defaultStoreId.value = null;
  defaultSiteId.value = null;

  storeObj.value = storesOptions.value.find(
    (store) => store.id === event.value,
  );
  defaultStoreId.value = storeObj.value.int_store_id;
  defaultSiteId.value = storeObj.value.id;
};

const showVoicePopup = ref(false);
const voiceStage = ref("listening"); // 'listening' | 'thinking'

const closeVoicePopup = () => {
  showVoicePopup.value = false;
  // Remove the 'Listening...' system message if present
  messages.value = messages.value.filter(
    (m) => m.content !== "Listening... (voice input)",
  );
  // Stop all queued and currently playing audios
  audioResponseQueue.length = 0;
  if (window._currentVoiceAudio) {
    window._currentVoiceAudio.pause();
    window._currentVoiceAudio.currentTime = 0;
    window._currentVoiceAudio = null;
  }
  // isPlayingAudio = false; // Will be used when audio response handling is implemented
  // Stop recording and close LiveKit connection
  stopRecording();
};

// Audio response queue (for future audio response handling via LiveKit)
const audioResponseQueue = [];
// let isPlayingAudio = false; // Will be needed when audio response handling is implemented

// Note: This function is currently not used but kept for future audio response handling
// When the backend sends audio_response type messages via LiveKit, uncomment and integrate
// const playNextAudioInQueue = () => {
////   isPlayingAudio = true;
//   // Set voiceStage to 'thinking' when playing a response
//   voiceStage.value = 'thinking';
//   const audioBlob = audioResponseQueue.shift();
//   const audioUrl = URL.createObjectURL(audioBlob);
//   const audio = new Audio(audioUrl);
//   window._currentVoiceAudio = audio;
//   audio.play();
//   audio.onended = () => {
//     isPlayingAudio = false;
//     window._currentVoiceAudio = null;
// if (audioResponseQueue.length > 0) {
//       playNextAudioInQueue();
//     } else {
//       // All audios finished, allow recording again
//       voiceStage.value = 'listening';
//       isRecording.value = false;
//     }
//   };
// }

const onImageError = (event) => {
  event.target.src = productPlaceholder;
};
</script>

<template>
  <AgentLayout>
    <!-- <div class="flex items-center">
        <h4 class="grid-header-title">Data Collection</h4>
        <button :title="checkLoggedInUserRole() ? 'You are not authorized to perform this action!': ''" :disabled="checkLoggedInUserRole()" @click="dataCollectionModal('show')" pill class="grid-header-action-btn">
          Add
        </button>
      </div> -->
    <PageHeader
      title="Agents Test"
      subtitle="You can select an agent and test its retrieval capabilities through interactive chat or voice input."
      :showButton="false"
    />

    <div class="config-section card">
      <!-- <div class="card-header">
            <h2>Key Insights</h2>
          </div> -->
      <div v-if="isLoading" class="agent-test-skeleton mb-3" aria-busy="true" aria-live="polite">
        <span class="visually-hidden">Loading…</span>
        <div class="agent-test-skeleton__header">
          <div class="skel-block skel-title"></div>
        </div>
        <div class="agent-test-skeleton__row grid grid-cols-12 gap-3 items-center">
          <div class="col-span-12 lg:col-span-5">
            <div class="skel-block skel-input"></div>
          </div>
          <div class="col-span-12 lg:col-span-5">
            <div class="skel-block skel-input"></div>
          </div>
          <div class="col-span-12 lg:col-span-2">
            <div class="skel-block skel-button"></div>
          </div>
        </div>
      </div>
      <div v-else>
        <div v-if="agentsArr && agentsArr.length">
          <div class="config-section mb-10">
            <div class="section-header mb-3 pl-0">
              <h2>Select Agent to Test</h2>
            </div>
            <div class="section-body">
              <div class="insights-grid grid grid-cols-12 gap-3 items-center">
                <!-- <div v-for="(agent, index) in agentsArr" :key="index" class="col-12 col-md-4 mb-4">
                    <div class="insight-card" @click="activeAgent(agent)" :class="{'insight-card-active' : agentObj.id == agent.id}">
                      <div class="insight-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            stroke-width="2" 
                            stroke-linecap="round" 
                            stroke-linejoin="round" 
                            class="lucide lucide-test-tube">
                          <path d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5h0c-1.4 0-2.5-1.1-2.5-2.5V2"></path>
                          <path d="M8.5 2h7"></path>
                          <path d="M14.5 16h-5"></path>
                          </svg>
                      </div>
                      <div class="insight-content">
                        <h3>{{ agent.name }}</h3>
                        <p>{{ agent.description }}</p>
                      </div>
                      <div class="insight-actions">
                        <span class="status-badge" :class="'status-'+agent.status">{{ agent.status_text }}</span>
                      </div>
                    </div>
                  </div> -->

                <div class="col-span-12 lg:col-span-5">
                  <!-- <div class="relative">
                    <span v-if="agentObj" class="multiselect-title-span"
                      >Agent</span
                    >
                    <VMultiselect
                      v-model.trim="agentObj"
                      :options="agentsArr"
                      :disabled="!agentsArr.length"
                      @select="changeAgent"
                      :searchable="true"
                      :close-on-select="true"
                      :show-labels="false"
                      placeholder="Agent"
                      trackBy="id"
                      label="name"
                    />
                  </div> -->

                  <FloatLabel class="w-full md:w-14rem">
                    <PDropdown
                      v-model="selectedAgentId"
                      inputId="selectedAgentId"
                      :options="agentsArr"
                      :disabled="!agentsArr.length"
                      optionLabel="name"
                      optionValue="id"
                      class="w-full"
                      @change="changeAgent($event)"
                      variant="filled"
                      filter
                      filterPlaceholder="Search by agent name"
                    >
                    </PDropdown>
                    <label for="selectedAgentId">Agent</label>
                  </FloatLabel>
                </div>

                <div class="col-span-12 lg:col-span-5">
                  <!-- <div class="relative">
                    <span v-if="storeObj" class="multiselect-title-span"
                      >Site</span
                    >
                    <VMultiselect
                      v-model.trim="storeObj"
                      :options="storesOptions"
                      :disabled="!agentObj"
                      @select="changeStore"
                      :searchable="true"
                      :close-on-select="true"
                      :show-labels="false"
                      placeholder="Site"
                      trackBy="id"
                      label="name"
                    />
                  </div> -->

                  <FloatLabel class="w-full md:w-14rem">
                    <PDropdown
                      v-model="selectedStoreId"
                      inputId="selectedStoreId"
                      :options="storesOptions"
                      :disabled="!agentObj"
                      optionLabel="name"
                      optionValue="id"
                      class="w-full"
                      @change="changeStore($event)"
                      variant="filled"
                      filter
                      filterPlaceholder="Search by site name"
                    >
                    </PDropdown>
                    <label for="selectedStoreId">Site</label>
                  </FloatLabel>
                </div>

                <div class="col-span-12 lg:col-span-2 flex items-center">
                  <button
                    @click="activeAgent()"
                    class="btn btn-primary add-button"
                    :disabled="!agentObj || !storeObj"
                  >
                    Go
                    <span
                      class="spinner-sm ml-2"
                      v-if="isLoadingToConnectToLiveKit"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="setting-seperator text-center mt-20 mb-20" />
          <div class="section-header mb-3 pl-0">
            <h2>The Selected Agent : {{ agentObj ? agentObj.name : "" }}</h2>
          </div>
          <ul
            v-if="superAdminEmail == userDetails.user.email"
            class="mb-40 configurations-tabs"
            id="myTab"
            role="tablist"
          >
            <div class="tab-item" :class="{ active: activeTab === 'messages' }" @click="activeTab = 'messages'" type="button">Messages</div>
            <div class="tab-item" :class="{ active: activeTab === 'logs' }" @click="activeTab = 'logs'" type="button">Logs</div>
          </ul>

          <!-- Tab panes -->
          <div class="tab-content">
            <div v-show="activeTab === 'messages'" role="tabpanel" tabindex="0">
              <div class="config-section">
                <div class="section-body mb-10">
                  <div class="testing-container">
                    <div class="testing-sidebar">
                      <div class="test-controls card">
                        <div class="conversation-header pl-0 pr-0">
                          <h2><i class="dx-icon-menu"></i>Conversations</h2>
                          <button
                            @click="createConversation()"
                            title="Create a new conversation"
                            type="button"
                            class="icon-button success"
                          >
                            <i class="bi bi-pencil-square cursor-pointer"></i>
                          </button>
                        </div>
                        <div
                          v-if="conversationsArr && conversationsArr.length"
                          class="question-list"
                        >
                          <button
                            v-for="(conversation, index) in conversationsArr"
                            :key="index"
                            class="question-btn"
                            :class="{
                              'question-btn-active':
                                conversationId === conversation.uuid,
                            }"
                            @click="fetchHistoryOfConversation(conversation)"
                          >
                            {{
                              conversation.title
                                ? conversation.title
                                : "Conversation " + (index + 1)
                            }}
                          </button>
                        </div>
                        <div v-else class="no-conversations">
                          <p class="text-center">
                            No conversations found. Start a new conversation.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      class="conversation-container card"
                      style="position: relative"
                    >
                      <div class="conversation-header pl-0 pr-0">
                        <h2>
                          <i class="dx-icon-comment"></i
                          >{{
                            currentConversation.title || "Conversation History"
                          }}
                        </h2>
                        <div class="header-info flex">
                          <!-- <span v-if="agentObj" class="status-badge" :class="'status-'+agentObj.status">{{ agentObj.status_text }}</span> -->

                          <!-- <span v-if="agentObj" class="status-badge" :class="'status-'+agentObj.status">

                              {{ agentObj.status_text }}
                            </span> -->

                          <span class="location-badge">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="lucide lucide-bot"
                            >
                              <path d="M12 8V4H8"></path>
                              <rect
                                width="16"
                                height="12"
                                x="4"
                                y="8"
                                rx="2"
                              ></rect>
                              <path d="M2 14h2"></path>
                              <path d="M20 14h2"></path>
                              <path d="M15 13v2"></path>
                              <path d="M9 13v2"></path>
                            </svg>
                            {{ agentObj ? agentObj.name : "" }}
                          </span>
                        </div>
                      </div>
                      <div
                        class="conversation-messages"
                        ref="messagesContainer"
                        @scroll="onMessagesScroll"
                      >
                        <!-- Go Button Overlay (shown when not connected) -->
                        <div v-if="!livekitConnected" class="connect-overlay">
                          <div class="connect-card">
                            <span class="connect-icon">
                              <i class="bi bi-broadcast-pin"></i>
                            </span>
                            <h3 class="connect-title">Ready to test</h3>
                            <p class="connect-sub">
                              Connect to
                              <b>{{ agentObj ? agentObj.name : "the agent" }}</b>
                              to start a live conversation.
                            </p>
                            <button
                              @click="startLiveKit"
                              :disabled="isLoadingToConnectToLiveKit"
                              class="connect-go-btn"
                            >
                              <i
                                v-if="!isLoadingToConnectToLiveKit"
                                class="bi bi-play-fill"
                              ></i>
                              {{ isLoadingToConnectToLiveKit ? "Connecting…" : "Go live" }}
                              <span
                                class="spinner-sm ml-2"
                                v-if="isLoadingToConnectToLiveKit"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </button>
                          </div>
                        </div>
                        <!-- Skeleton Loader for conversation history -->
                        <div v-if="isLoadingHistory" class="skeleton-loader">
                          <div class="skeleton-message-container agent-message">
                            <div class="skeleton-avatar"></div>
                            <div class="skeleton-message-content">
                              <div class="skeleton-bubble">
                                <div
                                  class="skeleton-line skeleton-line-1"
                                ></div>
                                <div
                                  class="skeleton-line skeleton-line-2"
                                ></div>
                                <div
                                  class="skeleton-line skeleton-line-3"
                                ></div>
                              </div>
                            </div>
                          </div>
                          <div class="skeleton-message-container user-message">
                            <div class="skeleton-message-content">
                              <div class="skeleton-bubble">
                                <div
                                  class="skeleton-line skeleton-line-short-1"
                                ></div>
                                <div
                                  class="skeleton-line skeleton-line-short-2"
                                ></div>
                              </div>
                            </div>
                          </div>
                          <div class="skeleton-message-container agent-message">
                            <div class="skeleton-avatar"></div>
                            <div class="skeleton-message-content">
                              <div class="skeleton-bubble">
                                <div
                                  class="skeleton-line skeleton-line-1"
                                ></div>
                                <div
                                  class="skeleton-line skeleton-line-2"
                                ></div>
                              </div>
                            </div>
                          </div>

                          <div class="skeleton-message-container user-message">
                            <div class="skeleton-message-content">
                              <div class="skeleton-bubble">
                                <div
                                  class="skeleton-line skeleton-line-short-1"
                                ></div>
                                <div
                                  class="skeleton-line skeleton-line-short-2"
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Actual messages -->
                        <div v-else>
                          <div
                            v-for="message in messages"
                            :key="message.id"
                            :class="{
                              'message-container': true,
                              'user-message': message.sender === 'user',
                              'agent-message': message.sender === 'agent',
                              'system-message': message.sender === 'system',
                            }"
                          >
                            <div
                              v-if="message.sender === 'agent'"
                              class="avatar"
                            >
                              <span>A</span>
                            </div>
                            <div class="message-content">
                              <div
                                v-if="message.type === 'map-image'"
                                class="map-image-container"
                              >
                                <a
                                  :href="message.image_url"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    :src="message.image_url"
                                    alt="Map"
                                    class="map-image"
                                    @error="onImageError"
                                  />
                                </a>
                              </div>
                              <div
                                v-else-if="message.type === 'attachment'"
                                class="attachment-card"
                              >
                                <a
                                  :href="message.product_url"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="attachment-link"
                                >
                                  <img
                                    v-if="message.image_url"
                                    :src="message.image_url"
                                    :alt="message.title"
                                    class="attachment-image"
                                  />
                                  <div class="attachment-title">
                                    {{ message.title }}
                                  </div>
                                </a>
                              </div>
                              <div
                                v-else-if="message.type === 'attachment-grid'"
                                class="attachment-grid"
                              >
                                <div
                                  v-for="attachment in message.attachments"
                                  :key="attachment.title"
                                  class="attachment-card grid-card"
                                >
                                  <a
                                    :href="attachment.product_url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="attachment-link"
                                  >
                                    <img
                                      v-if="attachment.image_url"
                                      :src="attachment.image_url"
                                      :alt="attachment.title"
                                      class="attachment-image"
                                      @error="onImageError"
                                    />

                                    <div class="attachment-title">
                                      {{ attachment.title }}
                                    </div>
                                    <span class="attachment-title"
                                      >Brand : {{ attachment.brand }}</span
                                    >
                                    <span class="attachment-title"
                                      >Price :
                                      {{
                                        Number(attachment.price).toFixed(2)
                                      }}</span
                                    >
                                  </a>
                                </div>
                              </div>
                              <div v-else class="message-bubble">
                                <div
                                  v-if="message.sender === 'agent'"
                                  class="markdown-content"
                                  v-html="
                                    processMessageContent(message.content)
                                  "
                                ></div>
                                <div v-else>
                                  {{ message.content }}
                                </div>
                              </div>
                              <div class="message-meta">
                                <span class="message-time">{{
                                  message.timestamp
                                }}</span>
                                <div
                                  v-if="message.sender === 'agent'"
                                  class="message-actions"
                                >
                                  <!-- <button class="icon-button-sm">
                                      <i class="dx-icon-volume"></i>
                                    </button>
                                    <button class="icon-button-sm">
                                      <i class="dx-icon-like"></i>
                                    </button>
                                    <button class="icon-button-sm">
                                      <i class="dx-icon-dislike"></i>
                                    </button> -->
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="typing-indicator" v-if="isTyping">
                          <div class="typing-bubble">
                            <AgentEmotion emotion="thinking" size="26" />
                          </div>
                        </div>
                      </div>
                      <div class="conversation-input">
                        <input
                          v-model.trim="userInput"
                          type="text"
                          placeholder="Type your message..."
                          class="message-input"
                          @keyup.enter="sendMessage"
                        />
                        <button
                          :disabled="!userInput"
                          class="send-button"
                          @click="sendMessage"
                        >
                          <i class="dx-icon-arrowright"></i>
                        </button>
                        <!-- <button class="mic-button active" @click="startRecording">
                            <i class="bi bi-mic"></i>
                          </button> -->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-show="activeTab === 'logs'"
              role="tabpanel"
              tabindex="0"
            >
              <div class="conversation-container card">
                <div class="conversation-header pl-0 pr-0">
                  <h2>
                    <i class="dx-icon-comment"></i
                    >{{ currentConversation.title || "Conversation History" }}
                  </h2>
                  <div class="header-info">
                    <span class="location-badge">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-bot"
                      >
                        <path d="M12 8V4H8"></path>
                        <rect width="16" height="12" x="4" y="8" rx="2"></rect>
                        <path d="M2 14h2"></path>
                        <path d="M20 14h2"></path>
                        <path d="M15 13v2"></path>
                        <path d="M9 13v2"></path>
                      </svg>
                      {{ agentObj ? agentObj.name : "" }}
                    </span>
                  </div>
                </div>
                <div class="logs-messages" ref="logsContainer">
                  <div
                    v-for="message in logs"
                    :key="message.id"
                    :class="{
                      'message-container': true,
                      'user-message': message.sender === 'user',
                      'agent-message': message.sender === 'agent',
                      'system-message': message.sender === 'system',
                    }"
                  >
                    <div class="message-content">
                      <div
                        v-if="message.type === 'attachment'"
                        class="attachment-card"
                      >
                        <a
                          :href="message.product_url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="attachment-link"
                        >
                          <img
                            v-if="message.image_url"
                            :src="message.image_url"
                            :alt="message.title"
                            class="attachment-image"
                          />
                          <div class="attachment-title">
                            {{ message.title }}
                          </div>
                        </a>
                      </div>
                      <div
                        v-else-if="message.type === 'attachment-grid'"
                        class="attachment-grid"
                      >
                        <div
                          v-for="attachment in message.attachments"
                          :key="attachment.title"
                          class="attachment-card grid-card"
                        >
                          <a
                            :href="attachment.product_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="attachment-link"
                          >
                            <img
                              v-if="attachment.image_url"
                              :src="attachment.image_url"
                              :alt="attachment.title"
                              class="attachment-image"
                            />
                            <div class="attachment-title">
                              {{ attachment.title }}
                            </div>
                          </a>
                        </div>
                      </div>
                      <div v-else class="message-bubble">
                        <div
                          v-if="message.sender === 'agent'"
                          class="markdown-content"
                          v-html="processMessageContentLogs(message.content)"
                        ></div>
                        <div v-else>
                          {{ message.content }}
                        </div>
                      </div>
                      <div class="message-meta">
                        <span class="message-time">{{
                          message.timestamp
                        }}</span>
                        <div
                          v-if="message.sender === 'agent'"
                          class="message-actions"
                        >
                          <!-- <button class="icon-button-sm">
                                      <i class="dx-icon-volume"></i>
                                    </button>
                                    <button class="icon-button-sm">
                                      <i class="dx-icon-like"></i>
                                    </button>
                                    <button class="icon-button-sm">
                                      <i class="dx-icon-dislike"></i>
                                    </button> -->
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Voice Assistant Popup -->
          <div v-if="showVoicePopup" class="voice-popup-overlay">
            <div class="voice-popup">
              <button
                class="voice-popup-close"
                @click="closeVoicePopup"
                aria-label="Close"
              >
                <span>&times;</span>
              </button>
              <div class="voice-circle-anim">
                <span v-if="voiceStage === 'listening'" class="mic-icon-anim">
                  <i class="bi bi-mic"></i>
                </span>
                <span v-else class="thinking-dots-loader">
                  <!-- Animated dots loader SVG from loading.io -->
                  <svg
                    width="60"
                    height="20"
                    viewBox="0 0 120 30"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#2680EB"
                  >
                    <circle cx="15" cy="15" r="15">
                      <animate
                        attributeName="r"
                        from="15"
                        to="15"
                        begin="0s"
                        dur="0.8s"
                        values="15;9;15"
                        calcMode="linear"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="fill-opacity"
                        from="1"
                        to="1"
                        begin="0s"
                        dur="0.8s"
                        values="1;.5;1"
                        calcMode="linear"
                        repeatCount="indefinite"
                      />
                    </circle>
                    <circle cx="60" cy="15" r="9" fill-opacity="0.3">
                      <animate
                        attributeName="r"
                        from="9"
                        to="9"
                        begin="0s"
                        dur="0.8s"
                        values="9;15;9"
                        calcMode="linear"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="fill-opacity"
                        from="0.5"
                        to="0.5"
                        begin="0s"
                        dur="0.8s"
                        values=".5;1;.5"
                        calcMode="linear"
                        repeatCount="indefinite"
                      />
                    </circle>
                    <circle cx="105" cy="15" r="15">
                      <animate
                        attributeName="r"
                        from="15"
                        to="15"
                        begin="0s"
                        dur="0.8s"
                        values="15;9;15"
                        calcMode="linear"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="fill-opacity"
                        from="1"
                        to="1"
                        begin="0s"
                        dur="0.8s"
                        values="1;.5;1"
                        calcMode="linear"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                </span>
              </div>
              <div class="voice-popup-text">
                <span v-if="voiceStage === 'listening'">Listening...</span>
                <span v-else>Thinking...</span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div
            v-if="!agentsArr || agentsArr.length == 0"
            class="no-data-available"
          >
            <p class="no-data-text">No agents available.</p>
          </div>
        </div>
      </div>
    </div>
  </AgentLayout>
</template>

<style lang="scss" scoped>
button {
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn {
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.btn:hover:not(:disabled) {
  transform: scale(1.05);
}

ul {
  padding-left: 0px !important;
}

.testing-container {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: var(--spacing-6);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

.test-controls {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-6);
  padding: var(--spacing-4);
  height: 100%;
}

.control-section {
  max-height: 580px;
  overflow-y: auto;
  padding-right: var(--spacing-1);

  /* Webkit scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: white !important;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--neutral-300);
    border-radius: 4px;

    &:hover {
      background-color: var(--neutral-400);
    }
  }

  h3 {
    font-size: var(--text-base);
    margin-bottom: var(--spacing-3);
  }
}

.voice-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .toggle-btn {
    position: relative;
    width: 44px;
    height: 22px;
    background-color: var(--neutral-300);
    border-radius: 11px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border: none;

    &.active {
      background-color: var(--primary-500);

      .toggle-slider {
        transform: translateX(22px);
      }
    }

    .toggle-slider {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      background-color: white;
      border-radius: 50%;
      transition: transform 0.2s ease;
    }
  }
}

.question-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  max-height: 500px;
  overflow-y: auto;
  padding-right: var(--spacing-1);

  /* Webkit scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: white !important;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--neutral-300);
    border-radius: 4px;

    &:hover {
      background-color: var(--neutral-400);
    }
  }

  .question-btn {
    text-align: left;
    padding: var(--spacing-2) var(--spacing-3);
    background-color: var(--neutral-50);
    border: 1px solid var(--neutral-200);
    border-radius: var(--border-radius-md);
    cursor: pointer;
    transition: all 0.2s ease;
    color: black !important;
    flex-shrink: 0;
    font-weight: normal !important;
    &:hover {
      background-color: var(--primary-50);
      border-color: var(--primary-200);
    }
  }

  .question-btn-active {
    background-color: #f3fcf9 !important;
    border-color: #98e1c9 !important;
    color: black !important;
    font-weight: 450 !important;
  }
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.conversation-container {
  display: flex;
  flex-direction: column;
  height: 700px;
  max-height: calc(100vh - 200px);
}

.conversation-header {
  padding: var(--spacing-4) var(--spacing-6);
  border-bottom: 1px solid var(--neutral-200);
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    margin: 0;
    font-size: var(--text-lg);

    i {
      color: var(--primary-500);
    }
  }
}

.location-badge {
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  padding: var(--spacing-1) var(--spacing-2);
  background-color: var(--primary-50);
  color: var(--primary-700);
  border-radius: var(--border-radius-md);
  font-size: var(--text-sm);

  i {
    font-size: 14px;
  }
}

.conversation-messages {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-4) var(--spacing-6);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  scroll-behavior: smooth;

  /* Webkit scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: white !important;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--neutral-300);
    border-radius: 4px;
  }
}

.logs-messages {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-4) var(--spacing-6);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  scroll-behavior: smooth;

  /* Webkit scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: white !important;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--neutral-300);
    border-radius: 4px;
  }

  /* Make log message bubbles full width for agent messages only */
  .message-container.agent-message {
    width: 100%;
    max-width: 100%;
    align-self: stretch !important;
  }
  .message-container.agent-message .message-bubble {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
}

.message-container {
  display: flex;
  gap: var(--spacing-3);

  &.user-message {
    align-self: flex-end;
    flex-direction: row-reverse;
    max-width: 85%;
    margin-left: auto;

    .message-bubble {
      background-color: var(--primary-500);
      color: white;
      border-radius: 18px 18px 4px 18px;
    }

    .message-meta {
      justify-content: flex-end;
    }
  }

  &.agent-message {
    align-self: flex-start;
    max-width: 85%;
    .message-bubble {
      background-color: var(--neutral-100);
      border-radius: 18px 18px 18px 4px;
    }
  }

  &.system-message {
    align-self: center;
    justify-self: center;
    max-width: 100%;
    width: 100%;
    justify-content: center;

    .message-bubble {
      background-color: var(--neutral-200);
      color: var(--neutral-700);
      border-radius: 18px;
      font-size: var(--text-xs);
      padding: var(--spacing-1) var(--spacing-3);
      text-align: center;
      margin: 0 auto;
      display: inline-block;
      max-width: fit-content;
    }
  }
}

.avatar {
  width: 36px;
  height: 36px;
  background-color: var(--primary-100);
  color: var(--primary-700);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
}

.message-bubble {
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: 4px;
  max-width: 100%;
  word-break: break-word;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);

  .message-time {
    font-size: var(--text-xs);
    color: var(--neutral-500);
  }

  .message-actions {
    display: flex;
    gap: var(--spacing-1);
  }
}

.icon-button-sm {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: none;
  border: none;
  color: var(--neutral-500);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--neutral-200);
    color: var(--neutral-700);
  }
}

.typing-indicator {
  align-self: flex-start;
  display: flex;
  gap: var(--spacing-3);
  margin-left: 51px;
}

.typing-bubble {
  padding: var(--spacing-2) var(--spacing-4);
  background-color: var(--neutral-100);
  border-radius: 18px;
  display: flex;
  align-items: center;
}

.conversation-input {
  padding: var(--spacing-4) var(--spacing-6);
  border-top: 1px solid var(--neutral-200);
  display: flex;
  gap: var(--spacing-2);
}

.message-input {
  flex: 1;
  padding: var(--spacing-3) var(--spacing-4);
  border: 1px solid var(--neutral-300) !important;
  border-radius: 24px;
  background-color: #ffffff;
  color: black;
  &:focus {
    outline: none;
    border-color: var(--primary-400);
  }
}

.send-button,
.mic-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.send-button {
  background-color: var(--primary-500);
  color: white;

  &:hover {
    background-color: var(--primary-600);
  }
}

.mic-button {
  background-color: var(--neutral-100);
  color: var(--neutral-600);

  &:hover {
    background-color: var(--neutral-200);
  }

  &.active {
    background-color: var(--primary-100);
    color: var(--primary-600);
  }
}

.voice-popup-overlay {
  position: fixed;
  z-index: 9999;
  inset: 0;
  background: rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}
.voice-popup {
  background: white;
  border-radius: 18px;
  box-shadow: 0 4px 32px rgba(0, 0, 0, 0.18);
  padding: 48px 64px 36px 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 340px;
  position: relative;
}
.voice-popup-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  background: #e53935;
  color: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  z-index: 2;
  padding: 0;
  transition: background 0.2s;
}
.voice-popup-close span {
  line-height: 1;
  display: block;
  margin-top: -1px;
}
.voice-popup-close:hover {
  background: #b71c1c;
}
.voice-circle-anim {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #e0f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  position: relative;
  animation: pulse 1.2s infinite;
}
.mic-icon-anim i {
  font-size: 3.2rem;
  color: var(--primary-color);
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(38, 128, 235, 0.3);
  }
  70% {
    box-shadow: 0 0 0 16px rgba(38, 128, 235, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(38, 128, 235, 0);
  }
}
.thinking-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}
.spinner-svg {
  width: 60px;
  height: 60px;
  animation: spinner-rotate 1s linear infinite;
}
@keyframes spinner-rotate {
  100% {
    transform: rotate(360deg);
  }
}
.spinner-svg .path {
  stroke-linecap: round;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
}
.voice-popup-text {
  font-size: 1.1rem;
  color: #333;
  margin-top: 4px;
  font-weight: 500;
}
.thinking-dots-loader {
  display: flex;
  align-items: center;
  justify-content: center;
}

.attachment-card {
  margin: 8px 0;
  padding: 0;
  background: var(--neutral-50);
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  max-width: 240px;
  overflow: hidden;
}
.attachment-link {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s;
}
.attachment-link:hover {
  box-shadow: 0 4px 16px rgba(38, 128, 235, 0.12);
  background: #eaf4ff;
}
.attachment-image {
  width: 100%;
  max-height: 140px;
  object-fit: cover;
  border-bottom: 1px solid #e0e0e0;
}
.attachment-title {
  padding: 3px 12px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #222;
}

.attachment-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 8px 0;
  max-width: 100%;
}

.map-image-container {
  margin: 8px 0;
  max-width: 100%;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.map-image {
  width: 100%;
  max-width: 480px;
  height: auto;
  display: block;
  margin: 0;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
}

@media (max-width: 768px) {
  .attachment-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .map-image {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .attachment-grid {
    grid-template-columns: 1fr;
  }
}

.attachment-card.grid-card {
  margin: 0;
  max-width: none;
  width: 100%;
  min-width: 160px;
}

/* Markdown styles for agent messages */
.markdown-content {
  // Markdown styling
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.5em 0 0.3em 0;
    font-weight: 600;
  }

  h1 {
    font-size: 1.5em;
  }
  h2 {
    font-size: 1.3em;
  }
  h3 {
    font-size: 1.1em;
  }
  h4,
  h5,
  h6 {
    font-size: 1em;
  }

  p {
    margin: 0.5em 0;
    line-height: 1.5;
  }

  ul,
  ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }

  li {
    margin: 0.2em 0;
  }

  blockquote {
    margin: 0.5em 0;
    padding: 0.5em 1em;
    border-left: 3px solid var(--primary-300);
    background-color: var(--primary-50);
    font-style: italic;
  }

  code {
    background-color: var(--neutral-100);
    padding: 0.1em 0.3em;
    border-radius: 3px;
    font-family: "Monaco", "Consolas", monospace;
    font-size: 0.9em;
  }

  pre {
    background-color: var(--neutral-100);
    padding: 1em;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.5em 0;

    code {
      background: none;
      padding: 0;
    }
  }

  strong,
  b {
    font-weight: 600;
  }

  em,
  i {
    font-style: italic;
  }

  a {
    color: var(--primary-600);
    text-decoration: underline;

    &:hover {
      color: var(--primary-700);
    }
  }

  hr {
    border: none;
    height: 1px;
    background-color: var(--neutral-300);
    margin: 1em 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.5em 0;

    th,
    td {
      border: 1px solid var(--neutral-300);
      padding: 0.5em;
      text-align: left;
    }

    th {
      background-color: var(--neutral-100);
      font-weight: 600;
    }
  }
}

/* Skeleton Loader Styles */
/* ── Agent-test page skeleton (replaces .spinner-border on initial load) ── */
.agent-test-skeleton {
  padding: 8px 0;
}

.agent-test-skeleton__header {
  margin-bottom: 16px;
}

.skel-block {
  background: linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-200) 50%, var(--neutral-100) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 8px;
}

.skel-title {
  width: 220px;
  max-width: 60%;
  height: 22px;
}

.skel-input {
  width: 100%;
  height: 48px;
}

.skel-button {
  width: 100%;
  height: 48px;
  border-radius: 10px;
}

.skeleton-loader {
  padding: 20px 0;
}

.skeleton-message-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 12px;
}

.skeleton-message-container.agent-message {
  flex-direction: row;
}

.skeleton-message-container.user-message {
  flex-direction: row-reverse;
  justify-content: flex-start;
}

.skeleton-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-200) 50%, var(--neutral-100) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  flex-shrink: 0;
}

.skeleton-message-content {
  flex: 1;
  max-width: 70%;
}

.skeleton-bubble {
  background: linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-200) 50%, var(--neutral-100) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 12px;
  padding: 12px 16px;
}

.skeleton-line {
  height: 14px;
  background: linear-gradient(90deg, var(--neutral-200) 25%, var(--neutral-300) 50%, var(--neutral-200) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-line:last-child {
  margin-bottom: 0;
}

.skeleton-line-1 {
  width: 85%;
}

.skeleton-line-2 {
  width: 70%;
}

.skeleton-line-3 {
  width: 60%;
}

.skeleton-line-short-1 {
  width: 45%;
}

.skeleton-line-short-2 {
  width: 35%;
}

.skeleton-message-container.user-message .skeleton-bubble {
  background: linear-gradient(90deg, var(--primary-50) 25%, var(--primary-100) 50%, var(--primary-50) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

.skeleton-message-container.user-message .skeleton-line {
  background: linear-gradient(90deg, var(--primary-100) 25%, var(--primary-200) 50%, var(--primary-100) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

@keyframes skeleton-loading {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* ── Dark mode: conversations sidebar ────────────────────── */
[data-theme="dark"] .question-list {
  &::-webkit-scrollbar-track {
    background-color: #1e2a3e !important;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #3d5070;

    &:hover {
      background-color: #4e6488;
    }
  }

  .question-btn {
    background-color: #28364e;
    border-color: #2d3f5a;
    color: #e8edf5 !important;

    &:hover {
      background-color: #324663;
      border-color: #3d5070;
    }
  }

  .question-btn-active {
    background-color: #1a2d52 !important;
    border-color: #4e7fff !important;
    color: #e8edf5 !important;
  }
}

[data-theme="dark"] .no-conversations {
  color: #9aadc4;
}

/* ============================================================
   ── Redesign layer — Agent Test console ──────────────────────
   Refines the live surfaces into a focused chat playground.
   Layered after the base rules and mirrors their selector
   specificity, so these win. Fully themed for light + dark.
   ============================================================ */

/* Outer card → seamless canvas */
.config-section.card { border-radius: 16px; }

/* Section titles become quiet eyebrows */
.section-header h2 {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin: 0;
}

/* ── Control bar (agent + site + Go) ── */
.insights-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  padding: 18px;
  background: var(--neutral-50);
  border: 1px solid var(--neutral-200);
  border-radius: 14px;
}
.insights-grid > div { flex: 1 1 240px; min-width: 0; }
.insights-grid > div:last-child { flex: 0 0 auto; }

.add-button.btn.btn-primary {
  height: 46px;
  padding: 0 28px;
  border-radius: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  color: #fff;
  background: linear-gradient(135deg, var(--primary-500), var(--primary-700));
  box-shadow: 0 8px 18px -8px var(--primary-600);
  transition: transform 0.15s ease, filter 0.15s ease;
}
.add-button.btn.btn-primary:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); }
.add-button.btn.btn-primary:disabled { opacity: 0.5; box-shadow: none; }

/* ── Tabs (Messages / Logs) ── */
.configurations-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--neutral-200);
  padding-left: 0 !important;
  margin-bottom: 24px !important;
}
.configurations-tabs .tab-item {
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  color: var(--neutral-500);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.2s ease, border-color 0.2s ease;
}
.configurations-tabs .tab-item:hover { color: var(--neutral-700); }
.configurations-tabs .tab-item.active {
  color: var(--primary-600);
  border-bottom-color: var(--primary-600);
}

/* ── Conversations rail ── */
.testing-container { gap: 20px; }
.test-controls.card {
  padding: 14px;
  gap: 14px;
  border-radius: 16px;
  border: 1px solid var(--neutral-200);
}
.test-controls .conversation-header {
  padding: 4px 4px 12px;
}
.test-controls .conversation-header h2 { font-size: 14px; }
.icon-button.success {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-50);
  color: var(--primary-600);
  border: 1px solid var(--primary-100);
  cursor: pointer;
  transition: all 0.2s ease;
}
.icon-button.success:hover { background: var(--primary-100); }
.question-list .question-btn {
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
}
.question-list .question-btn-active {
  background: var(--primary-50) !important;
  border-color: var(--primary-200) !important;
  color: var(--primary-700) !important;
  font-weight: 600 !important;
}

/* ── Chat canvas ── */
.conversation-container.card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--neutral-200);
}
.conversation-container .conversation-header {
  padding: 16px 20px;
  background: var(--neutral-50);
}
.conversation-container .conversation-messages {
  background:
    radial-gradient(640px 320px at 100% 0%, rgba(47, 68, 118, 0.045), transparent 60%),
    #ffffff;
}
.location-badge {
  border-radius: 9px;
  font-weight: 600;
  font-size: 12px;
}

/* message entrance */
.conversation-messages .message-container { animation: msg-in 0.25s ease both; }
@keyframes msg-in {
  from { opacity: 0; transform: translateY(6px); }
}

/* bubbles */
.avatar {
  background: linear-gradient(135deg, var(--primary-500), var(--primary-700));
  color: #fff;
  border-radius: 12px;
  font-weight: 700;
}
.message-bubble {
  padding: 11px 15px;
  line-height: 1.55;
  font-size: 14px;
}
.message-container.agent-message .message-bubble {
  background: var(--neutral-100);
  color: var(--neutral-800);
  border-radius: 16px 16px 16px 4px;
}
.message-container.user-message .message-bubble {
  background: linear-gradient(135deg, var(--primary-500), var(--primary-700));
  color: #fff;
  border-radius: 16px 16px 4px 16px;
}
.message-meta .message-time { font-size: 11px; }

/* typing */
.typing-bubble {
  background: var(--neutral-100);
  border-radius: 16px 16px 16px 4px;
}

/* composer */
.conversation-input {
  padding: 14px 18px;
  background: var(--neutral-50);
  gap: 10px;
}
.message-input {
  border-radius: 12px;
  border: 1px solid var(--neutral-300) !important;
  padding: 12px 16px;
  font-size: 14px;
}
.message-input:focus {
  border-color: var(--primary-400) !important;
  box-shadow: 0 0 0 3px var(--primary-50);
}
.send-button {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--primary-500), var(--primary-700));
}
.send-button:hover { filter: brightness(1.05); }
.send-button:disabled { opacity: 0.45; cursor: not-allowed; }

/* ── Connect overlay (replaces inline-styled Go box) ── */
.connect-overlay {
  position: absolute;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(3px);
}
.connect-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  max-width: 320px;
  padding: 28px 32px;
  background: #fff;
  border: 1px solid var(--neutral-200);
  border-radius: 16px;
  box-shadow: 0 18px 40px -18px rgba(15, 23, 42, 0.3);
}
.connect-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-size: 24px;
  color: #fff;
  background: linear-gradient(135deg, var(--primary-500), var(--primary-700));
  box-shadow: 0 8px 20px -8px var(--primary-600);
}
.connect-title { font-size: 17px; font-weight: 800; margin: 4px 0 0; color: var(--neutral-900); }
.connect-sub { font-size: 13px; color: var(--neutral-500); margin: 0; line-height: 1.5; }
.connect-sub b { color: var(--neutral-700); }
.connect-go-btn {
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 26px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, var(--primary-500), var(--primary-700));
  box-shadow: 0 10px 22px -10px var(--primary-600);
  transition: transform 0.15s ease, filter 0.15s ease;
}
.connect-go-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); }
.connect-go-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.connect-go-btn i { font-size: 18px; }

@media (prefers-reduced-motion: reduce) {
  .conversation-messages .message-container { animation: none; }
  .add-button.btn.btn-primary,
  .connect-go-btn { transition: none; }
}

/* ── Dark mode for the redesigned surfaces ── */
[data-theme="dark"] {
  .insights-grid { background: #1a2436; border-color: #2d3f5a; }
  .configurations-tabs { border-bottom-color: #2d3f5a; }
  .configurations-tabs .tab-item { color: #9aadc4; }
  .configurations-tabs .tab-item.active { color: #a8c4ff; border-bottom-color: #4e7fff; }

  .test-controls.card,
  .conversation-container.card { background: #1e2a3e; border-color: #2d3f5a; }
  .conversation-container .conversation-header,
  .conversation-input { background: #1a2436; border-color: #2d3f5a; }
  .conversation-container .conversation-messages {
    background:
      radial-gradient(640px 320px at 100% 0%, rgba(78, 127, 255, 0.06), transparent 60%),
      #16202f;
  }
  .conversation-messages::-webkit-scrollbar-track,
  .logs-messages::-webkit-scrollbar-track { background-color: #16202f !important; }
  .conversation-header h2 { color: #e8edf5; }
  .location-badge { background: #1a2d52; color: #a8c4ff; }

  .message-container.agent-message .message-bubble { background: #28364e; color: #e8edf5; }
  .typing-bubble { background: #28364e; }

  .message-input { background: #28364e; color: #e8edf5; border-color: #3d5070 !important; }
  .message-input::placeholder { color: #6b7f9c; }
  .message-input:focus { box-shadow: 0 0 0 3px rgba(78, 127, 255, 0.18); }

  .icon-button.success { background: #1a2d52; color: #a8c4ff; border-color: #274684; }

  .connect-overlay { background: rgba(11, 17, 28, 0.72); }
  .connect-card { background: #1e2a3e; border-color: #2d3f5a; }
  .connect-title { color: #e8edf5; }
  .connect-sub { color: #9aadc4; }
  .connect-sub b { color: #cdd9ea; }
}
</style>
